import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkle, Plus, Trash, CheckCircle, Clock, XCircle, ArrowsClockwise, PaperPlaneTilt, Database } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "bienvenida", label: "Bienvenida" },
  { value: "seguimiento", label: "Seguimiento" },
  { value: "promocion", label: "Promocion" },
  { value: "recordatorio", label: "Recordatorio" },
  { value: "cierre", label: "Cierre" },
  { value: "otro", label: "Otro" },
];

const META_STATUS = {
  APPROVED: { bg: "rgba(16,185,129,0.12)", text: "#10B981", icon: CheckCircle, label: "Aprobada" },
  PENDING: { bg: "rgba(245,158,11,0.12)", text: "#F59E0B", icon: Clock, label: "Pendiente" },
  REJECTED: { bg: "rgba(239,68,68,0.12)", text: "#EF4444", icon: XCircle, label: "Rechazada" },
  PAUSED: { bg: "rgba(161,161,170,0.12)", text: "#A1A1AA", icon: Clock, label: "Pausada" },
  NONE: { bg: "rgba(94,106,210,0.12)", text: "#9EA9FF", icon: Database, label: "Local" },
};

function MetaBadge({ status }) {
  const s = META_STATUS[status] || META_STATUS.NONE;
  const Icon = s.icon;
  return (
    <span className="px-2 py-0.5 text-[10px] rounded uppercase tracking-wider font-medium inline-flex items-center gap-1" style={{ background: s.bg, color: s.text }}>
      <Icon size={10} weight="fill" /> {s.label}
    </span>
  );
}

function SendTemplateDialog({ template, contacts, onSent }) {
  const [open, setOpen] = useState(false);
  const [contactId, setContactId] = useState("");
  const [params, setParams] = useState(Array(template.placeholder_count || 0).fill(""));
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!contactId) return toast.error("Selecciona contacto");
    setSending(true);
    try {
      await api.post("/templates/send", {
        contact_id: contactId, template_id: template.id, parameters: params,
      });
      toast.success("Plantilla enviada");
      setOpen(false);
      onSent && onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="h-7 bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid={`send-template-${template.id}`}>
          <PaperPlaneTilt size={12} className="mr-1" weight="fill" /> Enviar
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#121214] border-[#27272A] text-white">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Enviar "{template.name}"</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Contacto</Label>
            <Select value={contactId} onValueChange={setContactId}>
              <SelectTrigger className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="send-template-contact"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
              <SelectContent className="bg-[#121214] border-[#27272A] max-h-72">
                {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.phone})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {template.placeholder_count > 0 && (
            <div className="space-y-2">
              <Label>Variables ({template.placeholder_count})</Label>
              {Array.from({ length: template.placeholder_count }).map((_, i) => (
                <Input
                  key={i} value={params[i] || ""}
                  onChange={(e) => { const a = [...params]; a[i] = e.target.value; setParams(a); }}
                  placeholder={`{{${i + 1}}}`}
                  className="bg-[#0A0A0C] border-[#27272A] font-mono-jb text-xs"
                  data-testid={`template-param-${i}`}
                />
              ))}
            </div>
          )}
          <div className="bg-[#0A0A0C] border border-[#27272A] rounded p-3 text-xs text-[#A1A1AA] font-mono-jb whitespace-pre-wrap">
            {template.body}
          </div>
          <Button onClick={send} disabled={sending} className="w-full bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="send-template-submit">
            {sending ? "Enviando..." : "Enviar mensaje"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Templates() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", body: "", category: "otro" });
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useState("all");

  const load = async () => {
    const [t, c] = await Promise.all([api.get("/templates"), api.get("/contacts")]);
    setItems(t.data); setContacts(c.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/templates", form);
      toast.success("Plantilla creada");
      setOpen(false);
      setForm({ name: "", body: "", category: "otro" });
      load();
    } catch { toast.error("Error al crear"); }
  };

  const del = async (id) => {
    if (!confirm("Eliminar plantilla?")) return;
    await api.delete(`/templates/${id}`);
    load();
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/templates/sync-meta", {});
      toast.success(`${data.imported} importadas · ${data.updated} actualizadas`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Configura primero un numero WhatsApp con Business Account ID"); }
    finally { setSyncing(false); }
  };

  const filtered = items.filter((t) => {
    if (tab === "local") return t.source === "local" || !t.source;
    if (tab === "meta") return t.source === "meta";
    return true;
  });

  return (
    <div className="p-8 max-w-6xl mx-auto fade-in">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-2">Reutilizables</div>
          <h1 className="font-display text-4xl font-medium tracking-tighter">Plantillas</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">Mensajes locales o templates aprobados por Meta.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={sync} disabled={syncing} variant="outline" className="border-[#27272A] bg-[#121214] hover:bg-[#18181B]" data-testid="sync-meta-button">
            <ArrowsClockwise size={14} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Sincronizando..." : "Sincronizar Meta"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="new-template-button"><Plus size={16} className="mr-1.5" weight="bold" /> Nueva plantilla</Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121214] border-[#27272A] text-white">
              <DialogHeader><DialogTitle className="font-display tracking-tight">Nueva plantilla local</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0C] border-[#27272A] mt-1" data-testid="template-name" /></div>
                <div><Label>Categoria</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="bg-[#0A0A0C] border-[#27272A] mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121214] border-[#27272A]">
                      {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Contenido (usa {`{{1}}, {{2}}`} para variables Meta o {"{nombre}"} para campanas)</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} className="bg-[#0A0A0C] border-[#27272A] mt-1 font-mono-jb text-xs" data-testid="template-body" /></div>
                <Button onClick={create} className="w-full bg-[#5E6AD2] hover:bg-[#4B55C3]" data-testid="save-template-button">Guardar</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full mb-6">
        <TabsList className="bg-transparent border-b border-[#27272A] rounded-none p-0 h-auto justify-start gap-6">
          <TabsTrigger value="all" data-testid="tab-all" className="bg-transparent px-0 pb-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white text-[#71717A] data-[state=active]:border-b-2 data-[state=active]:border-[#5E6AD2]">Todas ({items.length})</TabsTrigger>
          <TabsTrigger value="local" data-testid="tab-local" className="bg-transparent px-0 pb-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white text-[#71717A] data-[state=active]:border-b-2 data-[state=active]:border-[#5E6AD2]">Locales</TabsTrigger>
          <TabsTrigger value="meta" data-testid="tab-meta" className="bg-transparent px-0 pb-3 rounded-none data-[state=active]:bg-transparent data-[state=active]:text-white text-[#71717A] data-[state=active]:border-b-2 data-[state=active]:border-[#5E6AD2]">Aprobadas Meta</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.length === 0 && (
          <div className="md:col-span-2 border border-dashed border-[#27272A] rounded-lg p-12 text-center text-[#71717A] text-sm">
            <Sparkle size={32} className="mx-auto mb-3 text-[#3F3F46]" />
            {tab === "meta" ? "Sin templates Meta. Configura un numero con Business Account ID y haz clic en Sincronizar." : "Sin plantillas. Crea la primera."}
          </div>
        )}
        {filtered.map((t) => (
          <div key={t.id} className="border border-[#27272A] bg-[#121214] rounded-lg p-5" data-testid={`template-${t.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-medium tracking-tight truncate">{t.name}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] uppercase tracking-wider text-[#71717A]">{t.category}</span>
                  {t.source === "meta" ? <MetaBadge status={t.meta_status} /> : <MetaBadge status="NONE" />}
                  {t.placeholder_count > 0 && <span className="text-[10px] text-[#9EA9FF] font-mono">·  {t.placeholder_count} var</span>}
                </div>
              </div>
              <button onClick={() => del(t.id)} className="p-1.5 rounded hover:bg-[#27272A] text-[#71717A] hover:text-[#EF4444]"><Trash size={14} /></button>
            </div>
            <div className="text-xs text-[#A1A1AA] bg-[#0A0A0C] p-3 rounded border border-[#27272A] font-mono-jb whitespace-pre-wrap min-h-[80px] mb-3">{t.body}</div>
            <div className="flex justify-end">
              <SendTemplateDialog template={t} contacts={contacts} onSent={load} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
